// Framework dependencies
import router from 'next/router';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useTracking } from 'react-tracking';

// Packages dependencies
import { TrackEventInput } from '@updater/ui-tracker';

// Repo dependencies
import { VerticalDetails } from 'components/vertical-details/VerticalDetails';
import { ItemKinds } from 'types';

// Local dependencies
import { SpinnerFullPage } from '@updater/ui-uds';
import { useFYMStatus } from 'flows/core/hooks/use-user-move-type';
import { DataProvider } from '../components/DataProvider';
import { Subtitle } from '../components/Subtitle';
import { TypeTwoFYMScreen } from '../components/TypeTwoFYMScreen';
import {
  FORWARD_MAIL_BASE_PATH,
  FORWARD_MAIL_FIRST_SCREEN,
  FORWARD_MAIL_RECEIPT_PATH,
} from '../constants';
import { DOMAIN, OBJECT, PAGE, VERB } from '../constants/analytics-tracker';
import { screens } from '../schemas';
import { useForwardMailUserDataStore } from '../store';
import { BooleanString } from '../types';
import { useRESTClient } from 'context';
import { useGetUserInfoQuery } from 'flows/core/hooks/use-get-user-info-query';

/**
 * @module FYMEntryPoints
 * @description Component that redirects to FYM flow depending on user data.
 * @returns {React.ReactElement}
 */
export const FYMEntryPoints = () => {
  const { addressesNormalized, id, resetUserData } =
    useForwardMailUserDataStore();
  const fymStatus = useFYMStatus();
  const userInfo = useGetUserInfoQuery();
  const { axios } = useRESTClient();
  const { Track, trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: DOMAIN.FORWARD_MAIL,
  });
  const suggestUSPSAlternative =
    !fymStatus.didUserClaimInvite ||
    fymStatus.forwardMailRequestCount >= 2 ||
    fymStatus.moveAgainData?.moveAgain;

  // route to first screen of fym flow if no forms exist
  const startFlow =
    !fymStatus.loading &&
    !fymStatus.forwardMailRequestCount &&
    !suggestUSPSAlternative;
  const isLoading = fymStatus.loading || startFlow;

  /**
   * Redirection logic.
   */
  useEffect(() => {
    if (startFlow) {
      router.replace(`${FORWARD_MAIL_BASE_PATH}${screens.currentAddress}`);
    }

    if (id) resetUserData();
    // We want to use the on load useEffect, but once the data is loaded,
    // which is why the useEffect depends ONLY from the loading flag
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, startFlow]);

  const targetURL = useMemo(() => {
    // if id and move is verify, go to receipt page
    if (id && fymStatus.forwardMailRequestCount) {
      return `${FORWARD_MAIL_RECEIPT_PATH}${id}`;
    }
    // if at least one of the addresses is not verified, go to current address screen
    if (addressesNormalized === BooleanString.FALSE) {
      return `${FORWARD_MAIL_BASE_PATH}${screens.currentAddress}`;
    }
    // else go to the address summary screen
    return FORWARD_MAIL_FIRST_SCREEN;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    // only track view event if there are submitted forms to show
    if (!isLoading)
      trackEvent({
        verb: VERB.VIEWED,
        pageName: PAGE.ENTRY_POINT,
        object: OBJECT.ENTRY_POINT,
      });
  }, [trackEvent, isLoading]);

  useEffect(() => {
    if (fymStatus?.didUserClaimInvite && !isLoading) {
      trackEvent({
        verb: VERB.VIEWED,
        pageName: PAGE.ENTRY_POINT,
        object: OBJECT.NON_INVITED_USER,
      });
    }
  }, [trackEvent, fymStatus?.didUserClaimInvite, isLoading]);

  const updateForwardMailDone = useCallback(async () => {
    const moveId = userInfo?.data?.getUser?.user.currentMove?.id;
    if (!moveId) return Promise.resolve();

    await axios.post(`/v2/moves/${moveId}/forward_mail/already_done`);
    return Promise.resolve();
  }, [axios, userInfo]);

  const onSelectForm = () => {
    trackEvent({
      verb: VERB.CLICKED,
      pageName: PAGE.ENTRY_POINT,
      object: OBJECT.COMPLETED_CARD,
    });
  };

  //show spinner if still loading
  if (isLoading) {
    return <SpinnerFullPage />;
  }

  // NON-INVITE USER - https://app.clickup.com/t/862ja6qpv
  // NOTE: Consider moving this to a component?
  if (suggestUSPSAlternative) {
    return <TypeTwoFYMScreen onDone={updateForwardMailDone} />;
  }

  return (
    <Track>
      <DataProvider>
        <VerticalDetails
          headline="Forward your mail"
          subtitle={<Subtitle />}
          type={ItemKinds.FORWARD_MAIL}
          target={targetURL}
          actionTracker={onSelectForm}
          startOverPath={
            addressesNormalized === BooleanString.FALSE
              ? `${FORWARD_MAIL_BASE_PATH}${screens.currentAddress}`
              : FORWARD_MAIL_FIRST_SCREEN
          }
        />
      </DataProvider>
    </Track>
  );
};
